/**
 * Compromise scope — the analyst attestation that on an attacker-in-the-middle case the
 * credentials and session token were CAPTURED but no attacker ACCESS to the account was
 * found (v1.182.0; design 2026-09-16, from IR-20260911-clarionhealth-001).
 *
 * Two values, an enum not a boolean so a third state can be added later:
 *   'access_confirmed'                        — the default / absent case: the reports read as
 *                                               they always have (an attacker had access).
 *   'credentials_captured_no_access_found'    — the attestation: creds/token captured, no
 *                                               observed attacker access. Softens the wording.
 *
 * 🔴 THE ATTESTATION LICENSES THE MOST REASSURING CLAIM A REPORT MAKES, so it is admin-gated
 * at UpdateCase (like `ualReviewed`), server-would-honour-it-only-with-no-malicious-IP, and
 * disclosed as the analyst's determination — never "the attacker did not access the account".
 *
 * This module is the GATE the UI shows before the attestation can be recorded. It is a
 * NEGATIVE FINDING ("no access"), and this project's rule for a negative is: only claim it
 * when the source could have shown the attacker and you actually looked. So the gate is a
 * checklist of coverage/attribution preconditions, each shown to the analyst as met/unmet.
 *
 * Pure and dependency-free so it can be unit-tested and imported anywhere.
 */

export const SCOPE_ACCESS_CONFIRMED = 'access_confirmed';
export const SCOPE_NO_ACCESS = 'credentials_captured_no_access_found';

export const COMPROMISE_SCOPES = [SCOPE_ACCESS_CONFIRMED, SCOPE_NO_ACCESS];

export function isValidCompromiseScope(v) {
  return COMPROMISE_SCOPES.includes(v);
}

/** Absent / unknown reads as the default — the prior behaviour, unchanged. */
export function normalizeCompromiseScope(v) {
  return v === SCOPE_NO_ACCESS ? SCOPE_NO_ACCESS : SCOPE_ACCESS_CONFIRMED;
}

export function isNoAccessScope(v) {
  return normalizeCompromiseScope(v) === SCOPE_NO_ACCESS;
}

function isSuccess(evt) {
  if (!evt) return false;
  // The non-interactive feed is pre-aggregated per (session, IP, hour) and carries counts.
  if (Number(evt.SuccessCount) > 0) return true;
  const status = String(evt.ResultStatus || evt.Status || '').toLowerCase().trim();
  return status === 'success' || status === 'succeeded';
}

/** An IP is "classified" for the gate iff the analyst gave it a non-default label. */
function isClassified(ip, ipClassifications) {
  const v = ipClassifications ? ipClassifications[ip] : undefined;
  return v === 'safe' || v === 'suspect' || v === 'malicious';
}

/**
 * Compute whether the "no attacker access" attestation may be recorded, and why not.
 *
 * @param {Object}  p
 * @param {Object}  p.attackerWindow          calculateAttackerWindow() result (or null)
 * @param {Object}  p.ipClassifications       { ip: 'safe'|'suspect'|'malicious' }
 * @param {Array}   p.signInEvents            results.SignInEvents (interactive)
 * @param {Array}   p.nonInteractiveEvents    results.NonInteractiveSignInEvents
 * @param {boolean} p.nonInteractiveReviewed  the non-interactive feed was retrieved AND the
 *                                             analyst ran the Non-interactive sign-ins panel
 *                                             (collected key present AND envelope persisted)
 * @returns {{ eligible: boolean, checks: Array<{key,label,met,detail?}> }}
 */
export function computeNoAccessEligibility({
  attackerWindow = null,
  ipClassifications = {},
  signInEvents = [],
  nonInteractiveEvents = [],
  nonInteractiveReviewed = false,
} = {}) {
  const cls = ipClassifications || {};

  // 1. A successful phishing-kit landing anchors the case. This is the whole premise:
  //    credentials/token were captured. `anchoredByPhishingKit` already means zero malicious
  //    IPs AND ≥1 successful kit leg (attackerWindow.js v1.181.0).
  const kitAnchor =
    !!attackerWindow &&
    attackerWindow.anchoredByPhishingKit === true &&
    (Number(attackerWindow.successfulPhishingKitLegCount) || 0) > 0;

  // 2. No IP classified Malicious. A malicious classification IS "access determined", so the
  //    two cannot coexist — malicious wins. (Independent of attackerWindow so the reason
  //    reads clearly even before the window is computed.)
  const maliciousIps = Object.keys(cls).filter((ip) => cls[ip] === 'malicious');
  const noMalicious = maliciousIps.length === 0;

  // 3. No IP left Suspect. A lingering "suspect" contradicts "no access found".
  const suspectIps = Object.keys(cls).filter((ip) => cls[ip] === 'suspect');
  const noSuspect = suspectIps.length === 0;

  // 4. Every address with a SUCCESSFUL sign-in (interactive OR non-interactive) is
  //    classified. That is where captured access would show up being used, so an
  //    unclassified success address is an address nobody has ruled on. Deliberately NOT
  //    "every non-service IP" — on a large case that is dominated by mail-relay and
  //    resolved-proxy addresses nobody would ever mark (measured: up to 84% of the tab).
  const successAddrs = new Set();
  for (const e of signInEvents || []) if (isSuccess(e) && e.IPAddress) successAddrs.add(e.IPAddress);
  for (const e of nonInteractiveEvents || []) if (isSuccess(e) && e.IPAddress) successAddrs.add(e.IPAddress);
  const unclassifiedSuccess = [...successAddrs].filter((ip) => !isClassified(ip, cls));
  const signInsClassified = unclassifiedSuccess.length === 0;

  // 5. The non-interactive feed was retrieved and reviewed. Token replay lives ONLY in that
  //    feed, so "no access" is not a defensible negative until it has been looked at.
  const niReviewed = !!nonInteractiveReviewed;

  const checks = [
    {
      key: 'kitAnchor',
      met: kitAnchor,
      label: 'A successful phishing-kit sign-in anchors the case',
      detail: kitAnchor ? '' : 'This mode is only for a confirmed attacker-in-the-middle capture with no attacker IP.',
    },
    {
      key: 'noMalicious',
      met: noMalicious,
      label: 'No IP is classified Malicious',
      detail: noMalicious ? '' : `A malicious classification means access was determined (${maliciousIps.length}).`,
    },
    {
      key: 'noSuspect',
      met: noSuspect,
      label: 'No IP is left classified Suspect',
      detail: noSuspect ? '' : `Resolve the ${suspectIps.length} address(es) still marked Suspect.`,
    },
    {
      key: 'signInsClassified',
      met: signInsClassified,
      label: 'Every address with a successful sign-in is classified',
      detail: signInsClassified ? '' : `Classify: ${unclassifiedSuccess.slice(0, 6).join(', ')}${unclassifiedSuccess.length > 6 ? ` and ${unclassifiedSuccess.length - 6} more` : ''}.`,
    },
    {
      key: 'nonInteractiveReviewed',
      met: niReviewed,
      label: 'Non-interactive sign-ins retrieved and reviewed',
      detail: niReviewed ? '' : 'Run the Non-interactive sign-ins panel on the IP Addresses tab first.',
    },
  ];

  return { eligible: checks.every((c) => c.met), checks };
}
